import Facade from '@/Facade'
import {
    BigWinCounterData,
    FreeSpinsActivateData,
    ShowBigWinData,
    ShowRegularWinningsData,
    WinCombination,
} from '@/modules'
import GameSignals from '@/signals/GameSignals'
import gsap from 'gsap'
import { Model } from '@/model/Model'
import { toMajorUnits } from '@/utils/CurrencyUnits'

export class WinningManager {
    private static readonly WIN_LAYER_MULTIPLIER: number = 2
    private static readonly WIN_LAYER_FADE_OUT: number = 30_000
    private _isWinLayerActive: boolean
    private _winLayerTimerId: number
    private _bigWinCounters: BigWinCounterData[]
    private _tlNumbers: gsap.core.Timeline
    private _bigWinValue: number
    private _model: Model
    private readonly _tweenObj: { value: number }

    public constructor(model: Model) {
        GameSignals.showRegularWin.connect(this.onShowRegularWin)
        GameSignals.bigWinPresentation.connect(this.onBigWinPresentation)
        GameSignals.skipBigWin.connect(this.onSkipBigWin)
        GameSignals.showBigWinComplete.connect(this.onShowBigWinComplete)
        GameSignals.activateScatters.connect(this.onActivateScatters)

        this._model = model
        this._bigWinCounters = []
        this._tweenObj = { value: 0 }
    }

    private onActivateScatters = (data: FreeSpinsActivateData) => {
        if (!data.winCurrency) {
            return
        }

        const combination = {
            pay_multiplier: 0,
            symbol_count: 0,
            symbol_id: '',
            symbols_positions: data.symbolsPositions,
            win_coin: 0,
            base_win_currency: data.baseWinCurrency,
            win_currency: data.winCurrency - data.baseWinCurrency,
        }

        GameSignals.showWinLabels.emit([combination])
    }

    private onShowRegularWin = (data: ShowRegularWinningsData) => {
        GameSignals.showWinLabels.emit(data.combinations)

        let winAmount: number = data.win
        if (this._model.winCounter.totalWin + winAmount > 10000 * this._model.currentBet) {
            winAmount = 10000 * this._model.currentBet - this._model.winCounter.totalWin
        }

        GameSignals.updateWinPanelValue.emit(toMajorUnits(winAmount))

        if (data.win / data.bet >= WinningManager.WIN_LAYER_MULTIPLIER) {
            //Activates additional win music
            this.onActivateWinLayer()
        }
    }

    private onBigWinPresentation = (data: ShowBigWinData) => {
        this._bigWinValue = data.win
        const bigWinObj = Facade.winningsConfig.BIG_WIN
        this._tlNumbers = gsap.timeline()
        this._tweenObj.value = 0

        this._tlNumbers.eventCallback('onComplete', () => {
            this._bigWinValue = null
            this._tlNumbers.kill()
            this._tlNumbers = null
            GameSignals.playBigWinEndSound.emit()
        })

        for (let i = 0; i <= data.bigWinLevel; i++) {
            const amount: number = i === data.bigWinLevel ? data.win : data.bet * bigWinObj[i].to
            const time: number = bigWinObj[i].time
            const easing: string = bigWinObj[i].easing

            this._tlNumbers.to(this._tweenObj, {
                value: amount,
                delay: 0.5,
                duration: time,
                ease: easing,
                onStart: () => {
                    if (i > 0) {
                        GameSignals.startBigWinLevel.emit()
                    }
                },
            })
        }

        GameSignals.showBigWin.emit({
            win: toMajorUnits(data.win),
            bet: toMajorUnits(data.bet),
            level: data.bigWinLevel,
        })
        this.createBigWinCounters(data.win, data.bet, data.bigWinLevel)
    }

    private onBigWinCounter = (counter: BigWinCounterData) => {
        const index = this._bigWinCounters.indexOf(counter)

        if (index !== -1) {
            this._model.winCounter.countWin(counter.amount, counter.duration / 1000)
            this._bigWinCounters.splice(index, 1)
        }
    }

    private onSkipBigWin = () => {
        let totalAmount = 0

        if (this._tlNumbers) {
            this._tweenObj.value = this._bigWinValue
            this._tlNumbers.vars.onComplete()
        }

        while (this._bigWinCounters.length > 0) {
            const counter = this._bigWinCounters.shift()
            totalAmount += counter.amount
            window.clearTimeout(counter.timerId)
        }

        this._model.winCounter.countWin(totalAmount, 0, true)
    }

    private onShowBigWinComplete = () => {
        GameSignals.bigWinSoundsComplete.emit()
    }

    private createBigWinCounters(win: number, bet: number, bigWinLevel: number) {
        const bigWinConfig = Facade.winningsConfig.BIG_WIN

        let prevDuration = 0
        let prevAmount = 0
        let totalAmount = 0

        for (let level = 0; level <= bigWinLevel; level++) {
            const config = bigWinConfig[level]

            totalAmount = level === bigWinLevel ? win : config.to * bet

            const counterDelay = prevDuration + 500
            const duration = config.time * 1000

            const counter: BigWinCounterData = {
                timerId: -1,
                amount: totalAmount - prevAmount,
                duration,
            }

            counter.timerId = window.setTimeout(this.onBigWinCounter, counterDelay, counter)
            this._bigWinCounters.push(counter)

            prevDuration = counterDelay + duration
            prevAmount = totalAmount
        }
    }

    private onActivateWinLayer = () => {
        if (this._model.roundState.isFreeSpins) {
            // no need to trigger win layer during free spins
            return
        }

        if (this._isWinLayerActive) {
            // resetting timer if an additional win occurred
            window.clearTimeout(this._winLayerTimerId)
            this._winLayerTimerId = window.setTimeout(this.onDeactivateWinLayer, WinningManager.WIN_LAYER_FADE_OUT)
            return
        }

        this._winLayerTimerId = window.setTimeout(this.onDeactivateWinLayer, WinningManager.WIN_LAYER_FADE_OUT)
        GameSignals.startWinLayerMusic.emit()
        this._isWinLayerActive = true
    }

    private onDeactivateWinLayer = () => {
        this._isWinLayerActive = false
        GameSignals.stopWinLayerMusic.emit()
        this._winLayerTimerId = -1
    }
}
